/**
 * Reports → Sickness (Reports step 4, DESIGN-reports.md; Rob's decisions 12/10/2026).
 *
 * ⚠️ HEALTH DATA. The server decides who may open this (admins, and managers with "Can view
 * sickness" on top of "Can view reports", inside their reach) and answers 403 to anyone
 * else — this page just says so. No reason is ever on the wire.
 *
 *  - The last 12 months up to today by default (4a), with Choose dates.
 *  - The Bradford score as a number, highest first, with no warning levels (3a).
 *  - Click a day of the week, or a person, to see what's behind it (decision J).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, X } from 'lucide-react';
import { toast } from 'sonner';
import { reportsApi, apiErrorMessage, saveBlobResponse } from '../../lib/api';
import { formatDateGB } from '../../lib/dates';
import { absenceWhen, bradfordSum, daysWord } from '../../lib/sicknessWords';
import {
  ReportFrame, Card, Chip, PillSelect, PeriodPicker, DownloadMenu, PrintButton, InfoTip, fmtDays, HEX,
  ScrollTable,
} from '../../components/reports/ReportUi';

const PAGE_DOWNLOADS = [
  { value: 'xlsx', label: 'Excel (people, absences, by day and month)' },
  { value: 'csv_people', label: 'CSV: people' },
  { value: 'csv_absences', label: 'CSV: every absence' },
];

const Stat = ({ label, value, sub, navy = false }) => (
  <Card className={`h-full ${navy ? 'relative overflow-hidden !bg-hive-navy !border-hive-navy text-white dark:!bg-hive-navy' : ''}`}>
    {navy && <div className="absolute -right-5 -top-4 w-24 h-24 bg-hive-orange opacity-[.12]" style={{ clipPath: HEX }} />}
    <p className={`text-[12.5px] font-bold ${navy ? 'text-hive-muted-cool' : 'text-hive-muted dark:text-muted-foreground'}`}>{label}</p>
    <p className="font-poppins text-[28px] font-bold leading-tight">{value}</p>
    {sub && <p className={`text-xs ${navy ? 'text-hive-muted-cool' : 'text-hive-muted dark:text-muted-foreground'}`}>{sub}</p>}
  </Card>
);

const NoSicknessAccess = () => (
  <Card className="max-w-xl mx-auto text-center py-10">
    <Lock className="w-6 h-6 mx-auto text-hive-muted" />
    <p className="font-poppins font-semibold text-lg mt-2">You don't have access to sickness</p>
    <p className="text-sm text-hive-muted dark:text-muted-foreground mt-1">
      It's health information. Ask an administrator if you need it — it's the "View sickness" switch.
    </p>
  </Card>
);

const BradfordTip = () => (
  <InfoTip label="About the Bradford score">
    The number of <b>separate absences</b>, squared, times the <b>days</b> off: 5 absences over 7 days is
    5 × 5 × 7 = 175. It weights lots of short absences far more than one long one. There are no warning
    levels: it's a prompt for a conversation, not a verdict, and needs care where absence may be linked to a
    disability or pregnancy. Sick days with only a weekend, bank holiday or non-working day between them count
    as <b>one</b> absence.
  </InfoTip>
);

const SicknessPage = () => {
  const [period, setPeriod] = useState({ start: null, end: null });
  const [filters, setFilters] = useState({ department_id: '', location_id: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [openPerson, setOpenPerson] = useState(null);
  const [personData, setPersonData] = useState(null);
  // By day of the week, or by month (Rob, 12/10/2026: "how about by month or over the year?")
  const [view, setView] = useState('weekday');     // weekday | month
  const [bar, setBar] = useState(null);            // the bar clicked: { kind, key }
  const [barData, setBarData] = useState(null);
  const [busy, setBusy] = useState(null);

  const baseParams = useMemo(() => {
    const p = period.start && period.end ? { start: period.start, end: period.end } : {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [period, filters]);
  const periodOnly = useMemo(
    () => (period.start && period.end ? { start: period.start, end: period.end } : {}), [period]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsApi.sickness(baseParams);
      setData(res.data);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
      else toast.error(apiErrorMessage(err, 'Could not load the report'));
    } finally {
      setLoading(false);
    }
  }, [baseParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOpenPerson(null); setBar(null); }, [baseParams]);
  useEffect(() => { setBar(null); }, [view]);

  useEffect(() => {
    if (!openPerson) return undefined;
    let cancelled = false;
    setPersonData(null);
    (async () => {
      try {
        const res = await reportsApi.sicknessPerson(openPerson, periodOnly);
        if (!cancelled) setPersonData(res.data);
      } catch (err) {
        if (!cancelled) toast.error(apiErrorMessage(err, 'Could not load their absences'));
      }
    })();
    return () => { cancelled = true; };
  }, [openPerson, periodOnly]);

  useEffect(() => {
    if (!bar) return undefined;
    let cancelled = false;
    setBarData(null);
    (async () => {
      try {
        const res = bar.kind === 'weekday'
          ? await reportsApi.sicknessWeekday(bar.key, baseParams)
          : await reportsApi.sicknessMonth(bar.key, baseParams);
        if (!cancelled) setBarData(res.data);
      } catch (err) {
        if (!cancelled) toast.error(apiErrorMessage(err, 'Could not load who that was'));
      }
    })();
    return () => { cancelled = true; };
  }, [bar, baseParams]);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await reportsApi.exportSickness({ ...baseParams, format: fmt });
      const name = saveBlobResponse(res, `sickness.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`);
      toast.success(`Downloaded ${name}`);
    } catch (err) {
      let message = 'Could not build the download';
      try {
        const text = await err?.response?.data?.text?.();
        if (text) message = JSON.parse(text).detail || message;
      } catch { /* keep the fallback */ }
      toast.error(message);
    } finally {
      setBusy(null);
    }
  };

  if (forbidden) return <ReportFrame><NoSicknessAccess /></ReportFrame>;

  const h = data?.headline;
  const bars = view === 'weekday'
    ? (data?.weekdays || []).map((w) => ({ key: w.weekday, short: w.day.slice(0, 3), long: w.day, days: w.days }))
    : (data?.months || []).map((m, i) => ({ key: m.key, short: m.month, long: m.label, days: m.days,
      year: (i === 0 || m.month === 'Jan') ? String(m.year) : '' }));
  const maxBar = Math.max(1, ...bars.map((b) => Number(b.days) || 0));
  const isTop = (b) => Number(b.days) > 0 && Number(b.days) === maxBar;

  return (
    <ReportFrame>
      <p className="text-[13px] text-hive-muted dark:text-muted-foreground mb-1 print:hidden">
        <Link to="/reports" className="hover:underline">Reports</Link> › <b className="text-hive-navy dark:text-foreground">Sickness</b>
      </p>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-poppins text-[26px] font-semibold">Sickness</h1>
          <p className="text-sm text-hive-muted dark:text-muted-foreground">
            {data ? `${data.scope.label} · ${data.period.label}${data.period.range ? ` (${data.period.range})` : ''}` : 'Loading…'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data && (
            <PeriodPicker
              period={data.period}
              backLabel="Back to last 12 months"
              onYear={() => setPeriod({ start: null, end: null })}
              onDates={(s, e) => setPeriod({ start: s, end: e })}
            />
          )}
          <PrintButton />
          <DownloadMenu options={PAGE_DOWNLOADS} busy={!!busy} onPick={download} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3 print:hidden">
        <PillSelect label="Department" value={filters.department_id} onChange={(v) => setFilters((f) => ({ ...f, department_id: v }))}>
          <option value="">All departments</option>
          {(data?.filters.departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </PillSelect>
        <PillSelect label="Location" value={filters.location_id} onChange={(v) => setFilters((f) => ({ ...f, location_id: v }))}>
          <option value="">All locations</option>
          {(data?.filters.locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </PillSelect>
      </div>

      <div className="flex items-center gap-2 rounded-xl bg-[#EEF1F7] dark:bg-muted text-[#33415e] dark:text-foreground px-4 py-2 text-[13px] mt-3"
           data-testid="sickness-health-note">
        <Lock className="w-4 h-4 shrink-0" />
        <span><b>Health information.</b> Only admins, and managers with "View sickness", can open this page. Reasons are never shown, and every download is logged.</span>
      </div>

      {data?.no_sickness_type && (
        <Card className="mt-4 !border-hive-orange" data-testid="sickness-no-type">
          <p className="font-poppins font-semibold">No leave type is marked as sickness yet</p>
          <p className="text-sm text-hive-muted dark:text-muted-foreground mt-1">
            This report can't see any sick days until one is. Go to <Link to="/settings?tab=leavetypes" className="text-hive-orange font-bold hover:underline">Settings → Leave Types</Link>,
            edit your sick leave type and tick <b>"This is sickness absence"</b>.
          </p>
        </Card>
      )}

      <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3.5 my-4 ${loading ? 'opacity-60' : ''}`}>
        <Stat navy label="Sick days" value={h ? daysWord(h.days) : '–'}
              sub={h ? `${h.people_sick} ${h.people_sick === 1 ? 'person' : 'people'}${h.people ? ` · ${fmtDays(h.per_person)} days per person across all ${h.people}` : ''}` : ''} />
        <Stat label="Separate absences" value={h ? h.absences : '–'}
              sub={h && h.absences ? `${fmtDays(h.days_per_absence)} days each on average` : 'none in this period'} />
        <Stat label="Long-term" value={h ? `${h.long_term} ${h.long_term === 1 ? 'person' : 'people'}` : '–'}
              sub={h ? `off 4 weeks or more in one go${h.long_term_off_now ? ` · ${h.long_term_off_now === h.long_term ? (h.long_term === 1 ? 'off now' : 'all off now') : `${h.long_term_off_now} off now`}` : ''}` : ''} />
        <Stat label="Off sick today" value={h ? `${h.off_today} ${h.off_today === 1 ? 'person' : 'people'}` : '–'}
              sub={h && h.off_today ? `${h.off_today_names.join(', ')}${h.off_today > h.off_today_names.length ? ` and ${h.off_today - h.off_today_names.length} more` : ''}` : ''} />
      </div>

      <Card className={loading ? 'opacity-60' : ''}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-poppins font-semibold text-[17px]">
            Sickness {view === 'weekday' ? 'by day of the week' : 'by month'}
          </p>
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-full border border-hive-line dark:border-border bg-hive-tile dark:bg-card p-0.5 text-[12.5px] font-bold print:hidden"
                 role="tablist" data-testid="sickness-chart-view">
              {[['weekday', 'By day of the week'], ['month', 'By month']].map(([k, label]) => (
                <button key={k} type="button" role="tab" aria-selected={view === k} onClick={() => setView(k)}
                        className={`rounded-full px-3 py-1 ${view === k ? 'bg-hive-navy text-white dark:bg-foreground dark:text-background' : 'text-hive-muted hover:text-hive-navy'}`}>
                  {label}
                </button>
              ))}
            </div>
            <span className="text-[12.5px] text-hive-muted dark:text-muted-foreground hidden sm:inline">
              Click a {view === 'weekday' ? 'day' : 'month'} to see who
            </span>
          </div>
        </div>
        <div className={`flex items-end ${view === 'weekday' ? 'gap-3 sm:gap-5' : 'gap-1 sm:gap-2'} h-[150px] mt-3 border-b border-hive-line dark:border-border`}
             data-testid={view === 'weekday' ? 'sickness-weekdays' : 'sickness-months'}>
          {bars.map((b) => (
            <button key={b.key} type="button" onClick={() => setBar((cur) => (cur?.key === b.key ? null : { kind: view, key: b.key }))}
                    className="flex-1 min-w-0 h-full flex flex-col items-center justify-end group" aria-label={`${b.long}: ${fmtDays(b.days)} days`}>
              <span className="text-[11px] sm:text-xs font-bold mb-1">{fmtDays(b.days)}</span>
              <span className={`block w-full max-w-[70px] rounded-t-lg transition-opacity group-hover:opacity-80 ${bar?.key === b.key ? 'ring-2 ring-hive-orange' : ''}`}
                    style={{ height: `${Math.max(2, (100 * (Number(b.days) || 0)) / maxBar)}%`,
                             background: isTop(b) ? '#E0655F' : '#EBA39F' }} />
            </button>
          ))}
        </div>
        <div className={`flex ${view === 'weekday' ? 'gap-3 sm:gap-5' : 'gap-1 sm:gap-2'} pt-1.5`}>
          {bars.map((b) => (
            <span key={b.key} className="flex-1 min-w-0 text-center text-[11px] sm:text-xs font-bold text-hive-muted dark:text-muted-foreground">
              {view === 'weekday'
                ? <><span className="hidden sm:inline">{b.long}</span><span className="sm:hidden">{b.short}</span></>
                : <>{b.short}{b.year && <span className="block font-normal">{b.year}</span>}</>}
            </span>
          ))}
        </div>

        {bar && (
          <div className="mt-3.5 rounded-2xl border border-hive-line dark:border-border bg-hive-tile dark:bg-card p-4" data-testid="sickness-day-drill">
            <div className="flex items-start justify-between gap-2">
              <p className="font-bold">
                {bar.kind === 'weekday' ? `Off sick on a ${barData?.day || '…'}` : `Off sick in ${barData?.label || '…'}`}
              </p>
              <button onClick={() => setBar(null)} aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            {!barData ? <p className="text-sm text-hive-muted mt-2">Loading…</p> : (
              <table className="w-full text-[13px] mt-2">
                <thead>
                  <tr className="text-[12px] text-hive-muted border-b border-hive-line dark:border-border">
                    <th className="text-left font-bold py-1.5">Person</th>
                    <th className="text-right font-bold px-2">Days</th>
                    <th className="text-left font-bold pl-3">Dates</th>
                  </tr>
                </thead>
                <tbody>
                  {barData.people.map((p) => (
                    <tr key={p.user_id} className="border-b border-hive-line-soft dark:border-border align-top">
                      <td className="py-1.5">{p.name}<span className="block text-[11.5px] text-hive-muted">{p.department}</span></td>
                      <td className="text-right px-2 tabular-nums">{fmtDays(p.days)}</td>
                      <td className="pl-3">
                        {bar.kind === 'month' && p.runs
                          ? p.runs.map((r) => (r.from === r.to ? formatDateGB(r.from) : `${formatDateGB(r.from)} – ${formatDateGB(r.to)}`)).join(', ')
                          : p.dates.map((d) => formatDateGB(d)).join(', ')}
                      </td>
                    </tr>
                  ))}
                  {!barData.people.length && <tr><td colSpan={3} className="py-3 text-hive-muted">Nobody.</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        )}
        <p className="text-[12.5px] text-hive-muted dark:text-muted-foreground mt-3">
          {view === 'weekday'
            ? <>Working days lost on each day of the week (a weekend day only shows if somebody works weekends). Mondays and
              Fridays are higher in most workplaces: it's a pattern worth a conversation, not proof of anything.</>
            : <>Working days lost in each month of the period, up to today. Winter is higher in most workplaces. For another
              year, use Choose dates.</>}
        </p>
      </Card>

      <Card className={`mt-3.5 ${loading ? 'opacity-60' : ''}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-poppins font-semibold text-[17px]">People, highest Bradford score first</p>
          <p className="text-[12.5px] text-hive-muted dark:text-muted-foreground">Click a person to see their absences</p>
        </div>
        <ScrollTable>
          <table className="w-full text-[13.5px] mt-2" data-testid="sickness-people">
            <thead>
              <tr className="text-[12px] text-hive-muted dark:text-muted-foreground border-b border-hive-line dark:border-border">
                <th className="text-left font-bold py-2 pr-2">Person</th>
                <th className="text-right font-bold px-2 whitespace-nowrap">Separate absences</th>
                <th className="text-right font-bold px-2">Days</th>
                <th className="text-right font-bold px-2 whitespace-nowrap">Bradford score <BradfordTip /></th>
                <th className="text-left font-bold px-2 whitespace-nowrap">Last absence</th>
                <th className="pl-2" />
              </tr>
            </thead>
            <tbody>
              {(data?.people || []).map((r) => (
                <React.Fragment key={r.user_id}>
                  <tr onClick={() => setOpenPerson((id) => (id === r.user_id ? null : r.user_id))}
                      className={`border-b border-hive-line-soft dark:border-border cursor-pointer hover:bg-hive-tile dark:hover:bg-muted ${
                        openPerson === r.user_id ? 'bg-hive-pending-bg dark:bg-muted' : ''}`}>
                    <td className="py-2 pr-2">
                      <b>{r.name}</b>
                      <span className="block text-[12px] text-hive-muted dark:text-muted-foreground">{r.department || 'No department'}</span>
                    </td>
                    <td className="text-right px-2 tabular-nums">{r.absences}</td>
                    <td className="text-right px-2 tabular-nums">{fmtDays(r.days)}</td>
                    <td className="text-right px-2 tabular-nums font-bold">{r.bradford}</td>
                    <td className="px-2 whitespace-nowrap">{r.off_now_since ? `since ${formatDateGB(r.off_now_since)}` : formatDateGB(r.last_absence)}</td>
                    <td className="pl-2">
                      {r.long_term && <Chip tone="pending">Long-term</Chip>}{' '}
                      {r.off_now_since && <Chip>Off now</Chip>}{' '}
                      {r.left && <Chip>Left</Chip>}
                    </td>
                  </tr>
                  {openPerson === r.user_id && (
                    <tr><td colSpan={6} className="pb-3 pt-1">
                      <div className="rounded-2xl border border-hive-line dark:border-border bg-hive-tile dark:bg-card px-4 py-3" data-testid="sickness-person-drill">
                        <p className="font-bold">{r.name} · {data?.period.label} · <span className="font-normal">{bradfordSum(r)}</span></p>
                        {!personData ? <p className="text-sm text-hive-muted mt-2">Loading…</p> : (
                          <table className="w-full text-[13px] mt-2">
                            <thead>
                              <tr className="text-[12px] text-hive-muted border-b border-hive-line dark:border-border">
                                <th className="text-left font-bold py-1.5">When</th>
                                <th className="text-left font-bold">Leave type</th>
                                <th className="text-right font-bold">Days</th>
                              </tr>
                            </thead>
                            <tbody>
                              {personData.absences.map((a) => (
                                <tr key={a.from} className="border-b border-hive-line-soft dark:border-border align-top">
                                  <td className="py-1.5">
                                    {absenceWhen(a)}
                                    {a.bookings > 1 && <span className="block text-[11.5px] text-hive-muted">{a.bookings} bookings with only non-working days between: one absence</span>}
                                  </td>
                                  <td>{a.leave_type} {a.long_term && <Chip tone="pending">Long-term</Chip>} {a.off_now && <Chip>Off now</Chip>}</td>
                                  <td className="text-right tabular-nums">{fmtDays(a.days)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
              {!(data?.people || []).length && (
                <tr><td colSpan={6} className="py-6 text-center text-hive-muted">No sickness in this period.</td></tr>
              )}
            </tbody>
          </table>
        </ScrollTable>
        <p className="text-[12.5px] text-hive-muted dark:text-muted-foreground mt-3">
          <b>The Bradford score</b> is the number of separate absences, squared, times the days off. It weights lots of short
          absences far more than one long one, which is why long-term sickness (4 weeks or more in one go) gets its own tag.
          There are no warning levels: it's a prompt for a conversation, not a verdict. Sickness is any leave type with
          "This is sickness absence" ticked; only approved days up to today count, each at what it cost when it was booked.
        </p>
      </Card>
    </ReportFrame>
  );
};

export default SicknessPage;
